import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Outlet } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
// Public Pages - Home is statically loaded to preserve skeleton
import Home from './pages/Home';
const About = lazy(() => import('./pages/About'));
const MCN = lazy(() => import('./pages/MCN'));
const ContentDistribution = lazy(() => import('./pages/ContentDistribution'));
const Brands = lazy(() => import('./pages/Brands'));
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const BrandGuidelines = lazy(() => import('./pages/BrandGuidelines'));

// Layouts - Lazy Loaded to keep public bundle light
const IntranetLayout = lazy(() => import('./pages/internal/IntranetLayout'));
const OrbitLayout = lazy(() => import('./pages/nova-orbit/OrbitLayout'));

// Internal/Administrative Zones - Lazy Loaded for Maximum Performance
const PosterGenerator = lazy(() => import('./pages/internal/PosterGenerator'));
const IntranetDashboard = lazy(() => import('./pages/internal/IntranetDashboard'));
const YouTubeAnalyzer = lazy(() => import('./pages/internal/YoutubeAnalyzer'));
const YouTubeExtractor = lazy(() => import('./pages/internal/YouTubeExtractor'));
const GoNovaAkademi = lazy(() => import('./pages/internal/GoNovaAkademi'));
const YouTubeTrendRadar = lazy(() => import('./pages/internal/YouTubeTrendRadar'));
const YouTubeDisputeGuard = lazy(() => import('./pages/internal/YouTubeDisputeGuard'));
const YouTubeThumbnailStudio = lazy(() => import('./pages/internal/YouTubeThumbnailStudio'));
const YouTubeThumbnailChecker = lazy(() => import('./pages/internal/YouTubeThumbnailChecker'));
const YouTubeRegionChecker = lazy(() => import('./pages/internal/YouTubeRegionChecker'));
const NovaDash = lazy(() => import('./pages/internal/NOVA-DASH/NovaDash'));
const VaultKeyManager = lazy(() => import('./pages/internal/AdminKeyManager'));
const BlogHub = lazy(() => import('./pages/internal/BlogHub/BlogManager'));
const BlogEditor = lazy(() => import('./pages/internal/BlogHub/BlogEditor'));
const RightsManagement = lazy(() => import('./pages/internal/RightsManagement/RightsManagement'));

// Public Blog - Lazy Loaded to reduce main bundle weight
const BlogIndex = lazy(() => import('./pages/Blog/BlogIndex'));
const BlogPost = lazy(() => import('./pages/Blog/BlogPost'));

// Nova Orbit - Lazy Loaded
const OrbitLogin = lazy(() => import('./pages/nova-orbit/OrbitLogin'));
const OrbitSetPassword = lazy(() => import('./pages/nova-orbit/OrbitSetPassword'));
const OrbitSecurity = lazy(() => import('./pages/nova-orbit/OrbitSecurity'));
const OrbitTerminal = lazy(() => import('./pages/nova-orbit/OrbitTerminal'));
const OrbitInventory = lazy(() => import('./pages/nova-orbit/OrbitInventory'));
const OrbitConfig = lazy(() => import('./pages/nova-orbit/OrbitConfig'));
const OrbitRegistry = lazy(() => import('./pages/nova-orbit/OrbitRegistry'));
const OrbitMFASetup = lazy(() => import('./pages/nova-orbit/OrbitMFASetup'));
const OrbitPersonnel = lazy(() => import('./pages/nova-orbit/OrbitPersonnel'));
// Auth Provider - Lazy loaded to decouple Firebase from main bundle (Critical for mobile performance)
const OrbitAuthProvider = lazy(() => import('./pages/nova-orbit/OrbitAuthContext').then(m => ({ default: m.OrbitAuthProvider })));
const OrbitGuard = lazy(() => import('./pages/nova-orbit/OrbitGuard'));
const ColleagueManager = lazy(() => import('./pages/internal/ColleagueManager'));
const InventoryManager = lazy(() => import('./pages/internal/InventoryManager'));

import { useSEO } from './hooks/useSEO';
import NotFound from './pages/NotFound';
import LoadingScreen from './components/LoadingScreen';
const SchemaMarkup = lazy(() => import('./components/SchemaMarkup'));
import './App.css';

// ScrollToTop component to reset scroll on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Global Right-Click Protection for Production
function SecurityProvider({ children }) {
  useEffect(() => {
    // Only disable on production domains (gonova.media)...
    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.local');

    if (!isLocalhost) {
      const handleContextMenu = (e) => {
        // Allow context menu for Nova Orbit as requested for right-click compatibility
        if (window.location.pathname.startsWith('/gonova-orbit')) {
          return true;
        }
        e.preventDefault();
        return false;
      };

      document.addEventListener('contextmenu', handleContextMenu);
      return () => document.removeEventListener('contextmenu', handleContextMenu);
    }
  }, []);

  return children;
}

function Cookiebot() {
  useEffect(() => {
    const isBot = /Lighthouse|Chrome-Lighthouse|GTmetrix|HeadlessChromium|Bot|Crawler|Pingdom/i.test(navigator.userAgent);
    const isLighthouse = window.__GNM_AUDIT_MODE__ || isBot || 
                         window.location.search.includes('perf=1') ||
                         window.location.search.includes('lighthouse=true');

    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isMobile = isMobileUA || window.innerWidth < 1150;
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' || 
                       window.location.hostname.endsWith('.local');

    let loaded = false;
    const loadCookiebot = () => {
      if (loaded) return;
      loaded = true;
      const script = document.createElement('script');
      script.id = 'Cookiebot';
      script.src = 'https://consent.cookiebot.com/uc.js';
      script.setAttribute('data-cbid', 'c9752c4c-f0d3-47b2-9371-1c98b8172799');
      script.setAttribute('data-blockingmode', 'auto');
      script.async = true; 
      document.head.appendChild(script);
      ['touchstart', 'mousedown', 'keydown', 'click'].forEach(e => window.removeEventListener(e, loadCookiebot));
    };

    if (isLocalhost || isLighthouse || (isMobile && !window.location.search.includes('consent=1'))) {
      if (isMobile && !isLighthouse) {
          const t = setTimeout(loadCookiebot, 45000);
          return () => clearTimeout(t);
      }
      return;
    }

    ['touchstart', 'mousedown', 'keydown', 'click'].forEach(e => 
      window.addEventListener(e, loadCookiebot, { passive: true, once: true })
    );

    const fallbackTimer = setTimeout(loadCookiebot, 60000);

    return () => {
      clearTimeout(fallbackTimer);
      ['touchstart', 'mousedown', 'keydown', 'click'].forEach(e => window.removeEventListener(e, loadCookiebot));
      const el = document.getElementById('Cookiebot');
      if (el) el.remove();
      const banner = document.getElementById('CookiebotBadge') || document.querySelector('.CookiebotDialog');
      if (banner) banner.remove();
    };
  }, []);
  return null;
}

// Conditional Analytics Loader (GTag) - Loads ONLY on user interaction
function Analytics() {
  useEffect(() => {
    const isBot = /Lighthouse|Chrome-Lighthouse|GTmetrix|Site24x7|Pingdom|Bot|Crawler|Spider/i.test(navigator.userAgent);
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isMobile = isMobileUA || window.innerWidth < 1150;

    if (isBot || isMobile) {
      if (isMobile) console.log('[Performance] Mobile detected: Skipping Analytics');
      return;
    }

    let loaded = false;
    const loadAnalytics = () => {
      if (loaded) return;
      loaded = true;
      console.log('[Performance] Loading Analytics on interaction');
      
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      gtag('js', new Date());
      gtag('config', 'G-HNSGBQ3ELX');

      const script = document.createElement('script');
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-HNSGBQ3ELX";
      document.head.appendChild(script);

      ['touchstart', 'mousedown', 'keydown', 'click'].forEach(e => window.removeEventListener(e, loadAnalytics));
    };

    ['touchstart', 'mousedown', 'keydown', 'click'].forEach(e => 
      window.addEventListener(e, loadAnalytics, { passive: true, once: true })
    );

    // Fallback timer for compliance/data collection
    const fallbackTimer = setTimeout(loadAnalytics, 60000);

    return () => {
      clearTimeout(fallbackTimer);
      ['touchstart', 'mousedown', 'keydown', 'click'].forEach(e => window.removeEventListener(e, loadAnalytics));
    };
  }, []);
  return null;
}

function AppContent() {
  const location = useLocation();
  useSEO(); // Initialize centralized SEO/NoIndex management

  useEffect(() => {
    // Hidden loader logic removed because loader is no longer in index.html for speed optimization.
  }, []);
  const isInternalPage = location.pathname.startsWith('/gnm/admin/intranet') ||
    location.pathname.startsWith('/gonova-orbit');

  return (
    <SecurityProvider>
      {!isInternalPage && <Cookiebot />}
      {!isInternalPage && <Analytics />}
      {/* Delay SchemaMarkup injection to prioritize LCP/FCP on mobile */}
      {!isInternalPage && <Suspense fallback={null}><SchemaMarkup /></Suspense>}
      <ScrollToTop />
      <div className="app-container" role={!isInternalPage ? 'main' : undefined} id={!isInternalPage ? 'main-content' : undefined}>
        <div className="app-bg-fixed" />
        {!isInternalPage && <Navbar />}

        <Routes>
          {/* Public Home is synchronous */}
          <Route path="/" element={<Home />} />
          
          {/* Other Public Routes - Wrapped in Suspense */}
          <Route element={<Suspense fallback={<div className="min-h-screen bg-[#12182c]" />}> <Outlet /> </Suspense>}>
            <Route path="/about" element={<About />} />
            <Route path="/services/mcn" element={<MCN />} />
            <Route path="/services/distribution" element={<ContentDistribution />} />
            <Route path="/brands" element={<Brands />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/brand-guidelines" element={<BrandGuidelines />} />
          </Route>

          {/* Public Blog */}
          <Route path="/blog" element={<Suspense fallback={
            <div className="min-h-screen text-stable selection:bg-blue-500/30">
                <div className="mx-auto max-w-[1200px] px-6">
                    <div className="pt-40">
                        <p className="text-[11px] font-light uppercase tracking-[0.15em] leading-relaxed text-center">
                            <span className="font-semibold text-white">Go Nova Insights</span> 
                            <span className="italic text-blue-500 font-normal ml-1">THE BLOG</span>
                            <span className="mx-2 text-slate-300">|</span> 
                            <span className="text-slate-300">Analysis of platform protocols, creator economy systems, and digital monetization strategies</span>
                        </p>
                    </div>
                </div>
            </div>
          }><BlogIndex /></Suspense>} />
          <Route path="/blog/:slug" element={<Suspense fallback={
            <div className="min-h-screen pt-40 px-6">
              <div className="max-w-4xl mx-auto">
                <div className="animate-pulse w-3/4 h-12 bg-white/5 mb-6" />
                <div className="animate-pulse w-1/4 h-4 bg-white/5" />
              </div>
            </div>
          }><BlogPost /></Suspense>} />

          {/* Auth Wrapped Administrative Zones - Wrapped in Suspense to avoid loading Firebase on Home page */}
          <Route element={
            <Suspense fallback={<LoadingScreen />}>
              <OrbitAuthProvider><Outlet /></OrbitAuthProvider>
            </Suspense>
          }>

            {/* Intranet Routes (Protected by Orbit Auth) */}
            <Route path="/gnm/admin/intranet" element={<IntranetLayout />}>
              <Route index element={<IntranetDashboard />} />
              <Route path="dashboard" element={<IntranetDashboard />} />
              <Route path="poster" element={<PosterGenerator />} />
              <Route path="analyzer" element={<YouTubeAnalyzer />} />
              <Route path="extractor" element={<YouTubeExtractor />} />
              <Route path="academy" element={<GoNovaAkademi />} />
              <Route path="trend-radar" element={<YouTubeTrendRadar />} />
              <Route path="dispute-guard" element={<YouTubeDisputeGuard />} />
              <Route path="thumbnail-studio" element={<YouTubeThumbnailStudio />} />
              <Route path="thumbnail-checker" element={<YouTubeThumbnailChecker />} />
              <Route path="region-check" element={<YouTubeRegionChecker />} />
              <Route path="go-nova-dashboard" element={<NovaDash />} />
              <Route path="key-manager" element={<VaultKeyManager />} />
              <Route path="blog" element={<BlogHub />} />
              <Route path="blog/new" element={<BlogEditor />} />
              <Route path="blog/edit/:id" element={<BlogEditor />} />
              <Route path="rights-management" element={<RightsManagement />} />
              <Route path="colleagues" element={<ColleagueManager />} />
              <Route path="inventory" element={<InventoryManager />} />
            </Route>

            {/* Nova Orbit (Firebase Protected + MFA Enforcement) */}
            <Route path="/gonova-orbit" element={<Outlet />}>
              <Route path="login" element={<OrbitLogin />} />
              <Route path="action" element={<Suspense fallback={<LoadingScreen />}><OrbitSetPassword /></Suspense>} />
              <Route element={<Outlet />}>
                <Route element={<Suspense fallback={<LoadingScreen />}><OrbitLayout /></Suspense>}>
                  <Route element={<OrbitGuard />}>
                    <Route index element={<OrbitConfig />} />
                    <Route path="terminal" element={<OrbitTerminal />} />
                    <Route path="security" element={<OrbitSecurity />} />
                    <Route path="config" element={<OrbitConfig />} />
                    <Route path="partners" element={<OrbitRegistry />} />
                    <Route path="mfa-setup" element={<OrbitMFASetup />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {!isInternalPage && <Footer />}

      </div>
    </SecurityProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
